import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";

import ResponsiveButton from "components/ResponsiveButton";
import { navigateTo } from "code/navigation";

interface SessionExpiredPageProps {
	/** Whether the blocking "session expired" overlay is shown. */
	open: boolean;
}

/**
 * Blocking overlay shown when the user's session has expired.
 *
 * Rendered by `components/SessionExpiryProvider.tsx` and driven purely by its `open` prop, which
 * flips true when `code/sessionRefresh.ts`'s `refreshAdmin2Session()` returns `false` — i.e. the
 * `POST /PortalFramework/Account/Refresh` call failed, so both `SWA-A` and `SWA-R` are gone and
 * there is no way to recover the session in the background.
 *
 * This is an overlay rather than a route, deliberately. On expiry Admin2 does *not* call SAL's
 * `logout()` and does *not* navigate anywhere, so `isLoggedIn` stays true and the user's page, URL
 * and in-progress work are all preserved underneath the dialog — which matters if they later sign
 * in again in another tab, and avoids the jarring bounce straight out to MyKR. See the
 * SessionExpiryProvider module comment for why the default SAL refresh handler is replaced.
 *
 * Deliberately cannot be dismissed except by signing in again: the session really is gone, and
 * letting the user click away would just leave them looking at a page that silently stops working.
 * The "Sign in again" action navigates to `/Account/Login`, which is the only way past it.
 */
export default function SessionExpiredPage({ open }: SessionExpiredPageProps) {
	return (
		<Dialog
			open={open}
			onClose={() => {
				// No-op: this dialog cannot be dismissed by backdrop click or Escape. The only way
				// out is the "Sign in again" action below.
			}}
			disableEscapeKeyDown
			maxWidth="xs"
			fullWidth
			aria-labelledby="session-expired-title"
			aria-describedby="session-expired-description"
		>
			<DialogContent sx={{ pt: 4 }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						textAlign: "center"
					}}
				>
					<AccessTimeIcon
						sx={{ fontSize: 112, color: "secondary.light50", lineHeight: 0, mb: 3 }}
					/>

					<Typography variant="display4" component="h1" id="session-expired-title" sx={{ mb: 1.5 }}>
						Your session has expired
					</Typography>

					<Typography variant="text16" id="session-expired-description">
						For your security, you&rsquo;ve been signed out. Please sign in again to continue. Any
						unsaved changes on this page will be lost.
					</Typography>
				</Box>
			</DialogContent>

			<DialogActions sx={{ justifyContent: "center", pb: 4, px: 3 }}>
				<ResponsiveButton variant="contained" onClick={() => navigateTo("/Account/Login")}>
					Sign in again
				</ResponsiveButton>
			</DialogActions>
		</Dialog>
	);
}
